import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Grid,
  Paper,
  Fade,
  Zoom,
  Divider,
  Chip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { 
  Close, 
  Analytics, 
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart
} from '@mui/icons-material';
import { PieChart, BarChart, LineChart } from '@mui/x-charts';
import { ModalData, ChartConfig } from '../types';

interface ChartModalProps {
  open: boolean;
  onClose: () => void;
  data: ModalData | null;
}

const ChartModal: React.FC<ChartModalProps> = ({ open, onClose, data }) => {
  const theme = useTheme();
  
  if (!data) return null;

  const renderChart = (config: ChartConfig, index: number) => {
    const chartProps = {
      height: 700,
      width: 600,
      slotProps: {
        legend: {
          direction: 'row' as const,
          position: { vertical: 'bottom' as const, horizontal: 'middle' as const },
          padding: 0,
          itemMarkWidth: 18,
          itemMarkHeight: 18,
          labelStyle: {
            fontSize: 18,
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
        },
      },
    };

    switch (config.type) {
      case 'doughnut':
      case 'pie':
        const total = config.data.datasets[0].data.reduce((sum, val) => sum + val, 0);
        const pieData = config.data.datasets[0].data.map((value, index) => ({
          id: index,
          value,
          label: `${config.data.labels[index]} (${((value / total) * 100).toFixed(1)}%)`,
          color: config.data.datasets[0].backgroundColor?.[index] || theme.palette.primary.main
        }));
        return (
          <PieChart 
            {...chartProps}
            series={[{ 
              data: pieData,
              highlightScope: { faded: 'global', highlighted: 'item' },
              faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
              arcLabel: (item) => `${((item.value / total) * 100).toFixed(1)}%`,
              arcLabelMinAngle: 20,
            }]}
            slotProps={{
              legend: {
                direction: 'row' as const,
                position: { vertical: 'bottom' as const, horizontal: 'middle' as const },
                padding: 0,
                itemMarkWidth: 18,
                itemMarkHeight: 18,
                labelStyle: {
                  fontSize: 20,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                },
              },
            }}
          />
        );
      case 'bar':
        const barSeries = config.data.datasets.map((dataset, datasetIndex) => ({
          data: dataset.data,
          label: dataset.label || `数据集 ${datasetIndex + 1}`,
          color: dataset.backgroundColor?.[0] || theme.palette.primary.main
        }));
        return (
          <BarChart 
            {...chartProps}
            series={barSeries}
            xAxis={[{ 
              data: config.data.labels,
              scaleType: 'band',
              tickLabelStyle: {
                fontSize: 16,
                fontWeight: 600,
                color: theme.palette.text.primary,
              },
            }]}
            yAxis={[{ 
              scaleType: 'linear',
              min: 0,
              tickLabelStyle: {
                fontSize: 16,
                fontWeight: 600,
                color: theme.palette.text.primary,
              },
            }]}
            slotProps={{
              legend: {
                direction: 'row' as const,
                position: { vertical: 'bottom' as const, horizontal: 'middle' as const },
                padding: -25,
                itemMarkWidth: 18,
                itemMarkHeight: 18,
                labelStyle: {
                  fontSize: 20,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                },
              },
            }}
          />
        );
      case 'line':
        const lineSeries = config.data.datasets.map((dataset, datasetIndex) => ({
          data: dataset.data,
          label: dataset.label || `数据集 ${datasetIndex + 1}`,
          color: '#000000',
          curve: 'linear' as const,
          strokeWidth: 4,
        }));
        return (
          <LineChart 
            {...chartProps}
            series={lineSeries}
            xAxis={[{ 
              data: config.data.labels,
              scaleType: 'band',
              tickLabelStyle: {
                fontSize: 16,
                fontWeight: 600,
                color: theme.palette.text.primary,
              },
            }]}
            yAxis={[{ 
              scaleType: 'linear',
              min: 0,
              tickLabelStyle: {
                fontSize: 16,
                fontWeight: 600,
                color: theme.palette.text.primary,
              },
            }]}
            slotProps={{
              legend: {
                direction: 'row' as const,
                position: { vertical: 'bottom' as const, horizontal: 'middle' as const },
                padding: -25,
                itemMarkWidth: 18,
                itemMarkHeight: 18,
                labelStyle: {
                  fontSize: 20,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                },
              },
            }}
          />
        );
      default:
        return null;
    }
  };

  const getChartIcon = (type: string) => {
    switch (type) {
      case 'doughnut':
      case 'pie':
        return <PieChartIcon />;
      case 'bar':
        return <BarChartIcon />;
      case 'line':
        return <ShowChart />;
      default:
        return <Analytics />;
    }
  };

  const getChartTitle = (index: number, total: number) => {
    if (total === 1) return '';
    
    const titles = [
      '工作日/周末分布',
      '时段分布',
      '星期分布'
    ];
    
    return titles[index] || `图表 ${index + 1}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Zoom}
      transitionDuration={300}
             PaperProps={{
         sx: {
           borderRadius: 2,
           maxHeight: '90vh',
           overflow: 'hidden',
           bgcolor: 'background.paper',
         },
       }}
    >
      <DialogTitle sx={{ pb: 1, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Analytics color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              {data.title}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose} 
            size="large"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

             <DialogContent sx={{ p: 3, bgcolor: 'background.default' }}>

        {data.charts && data.charts.length > 0 && (
          <Grid container spacing={3}>
            {data.charts.map((chart, index) => (
              <Grid item xs={12} md={data.charts!.length > 1 ? 6 : 12} key={index}>
                <Fade in timeout={600 + index * 200}>
                                     <Paper
                     elevation={2}
                     sx={{
                       p: 4,
                       borderRadius: 2,
                       bgcolor: 'background.paper',
                       border: '1px solid',
                       borderColor: 'divider',
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                       '&:hover': {
                         boxShadow: theme.shadows[8],
                         transform: 'translateY(-2px)',
                         borderColor: 'primary.main',
                       },
                       position: 'relative',
                       overflow: 'hidden',
                     }}
                   >
                    {data.charts!.length > 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                                 <Box sx={{ 
                           p: 1, 
                           borderRadius: 1, 
                           bgcolor: 'primary.main',
                           color: 'primary.contrastText',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           transition: 'all 0.2s ease',
                           '&:hover': {
                             bgcolor: 'primary.dark',
                           }
                         }}>
                           {getChartIcon(chart.type)}
                         </Box>
                        <Typography
                          variant="h6"
                          sx={{ 
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            flex: 1
                          }}
                        >
                          {getChartTitle(index, data.charts!.length)}
                        </Typography>
                                                 <Chip 
                           label={chart.type.toUpperCase()} 
                           size="small" 
                           color="primary" 
                           variant="outlined"
                           sx={{ 
                             fontWeight: 500,
                             borderRadius: 1,
                           }}
                         />
                      </Box>
                    )}
                                         <Box sx={{ 
                       height: 650, 
                       position: 'relative',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       borderRadius: 1,
                       bgcolor: 'background.default',
                       p: 4,
                       overflow: 'hidden',
                       mb: 3
                     }}>
                       {renderChart(chart, index)}
                     </Box>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

        {(!data.charts || data.charts.length === 0) && (
          <Fade in timeout={700}>
            <Box sx={{ 
              py: 4,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              px: 4
            }}>
              {data.title.includes('AI运营分析') ? (
                // AI 分析结果展示
                <Box>
                  {data.isLoading ? (
                    // 加载状态显示
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <CircularProgress 
                        size={60} 
                        thickness={4}
                        sx={{ 
                          color: 'primary.main',
                          mb: 3
                        }} 
                      />
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        正在调取 DeepSeek API
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ opacity: 0.8 }}>
                        正在分析运营数据，请稍候...
                      </Typography>
                    </Box>
                  ) : (
                    // AI 分析结果展示
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: 2, 
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Analytics sx={{ fontSize: 24 }} />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          AI 分析结果
                        </Typography>
                      </Box>
                      
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.8,
                          color: 'text.primary',
                          fontSize: '1.1rem',
                          '& strong': {
                            color: 'primary.main',
                            fontWeight: 600
                          },
                          '& .bullet-point': {
                            display: 'block',
                            marginLeft: '1.5rem',
                            position: 'relative',
                            '&::before': {
                              content: '"•"',
                              position: 'absolute',
                              left: '-1.5rem',
                              color: 'primary.main',
                              fontWeight: 'bold'
                            }
                          }
                        }}
                      >
                        {data.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                // 其他指标的无图表提示
                <Box sx={{ textAlign: 'center' }}>
                  <Analytics sx={{ fontSize: 64, color: 'text.secondary', mb: 3, opacity: 0.6 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    目前暂无图表数据
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                    该指标暂未配置图表展示
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChartModal; 